import { useState } from "react";
import {
  Badge,
  Box,
  CircularProgress,
  Divider,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Popover,
  Tooltip,
  Typography,
  Button,
} from "@mui/material";
import { NotificationsNone, Notifications } from "@mui/icons-material";
import { formatDistanceToNow, parseISO } from "date-fns";
import { fr, enUS } from "date-fns/locale";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useNotifications } from "@/features/notifications/hooks/useNotifications";
import { formatNotificationMessage } from "@/features/notifications/lib/formatNotificationMessage";
import { resolveNotificationTarget } from "@/features/notifications/lib/resolveNotificationTarget";
import type { AppNotification } from "@/features/notifications/types";

type NotificationBellProps = {
  collapsed?: boolean;
};

function formatRelativeTime(value: string, language: string): string {
  try {
    const date = parseISO(value);
    const locale = language.startsWith("fr") ? fr : enUS;
    return formatDistanceToNow(date, { addSuffix: true, locale });
  } catch {
    return "";
  }
}

export function NotificationBell({ collapsed = false }: NotificationBellProps) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { items, unreadCount, loading, refresh, markAllRead, markRead } = useNotifications();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);

  const handleOpen = async (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    await refresh({ reset: true });
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleMarkAllRead = async () => {
    await markAllRead();
  };

  const handleNotificationClick = async (item: AppNotification) => {
    if (!item.is_read) {
      await markRead(item.pk);
    }
    const target = resolveNotificationTarget(item);
    handleClose();
    navigate({
      pathname: target.pathname,
      search: target.search,
    });
  };

  const handleSeeMore = () => {
    handleClose();
    navigate("/notifications");
  };

  return (
    <>
      <Tooltip title={t("notifications.title")}>
        <IconButton
          size="small"
          onClick={handleOpen}
          aria-label={t("notifications.title")}
          sx={{
            ml: 0,
            flexShrink: 0,
            color: "text.primary",
            p: 0.75,
          }}
        >
          <Badge
            badgeContent={unreadCount}
            color="error"
            max={99}
            invisible={unreadCount <= 0}
            overlap="circular"
          >
            {unreadCount > 0 ? (
              <Notifications fontSize="small" />
            ) : (
              <NotificationsNone fontSize="small" />
            )}
          </Badge>
        </IconButton>
      </Tooltip>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{
          paper: {
            sx: {
              width: { xs: 300, sm: 360 },
              maxHeight: 420,
              display: "flex",
              flexDirection: "column",
            },
          },
        }}
      >
        <Box
          sx={{
            px: 2,
            py: 1.25,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
          }}
        >
          <Typography variant="subtitle1" fontWeight={700}>
            {t("notifications.title")}
          </Typography>
          {unreadCount > 0 ? (
            <Button size="small" onClick={handleMarkAllRead}>
              {t("notifications.markAllRead")}
            </Button>
          ) : null}
        </Box>
        <Divider />
        {loading && items.length === 0 ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
            <CircularProgress size={24} />
          </Box>
        ) : null}
        {!loading && items.length === 0 ? (
          <Box sx={{ px: 2, py: 3 }}>
            <Typography variant="body2" color="text.secondary" align="center">
              {t("notifications.noNotifications")}
            </Typography>
          </Box>
        ) : (
          <>
            <List dense disablePadding sx={{ overflow: "auto", flex: 1 }}>
              {items.map((item) => {
                const { title, body } = formatNotificationMessage(item, t);
                return (
                  <ListItemButton
                    key={item.pk}
                    alignItems="flex-start"
                    onClick={() => {
                      void handleNotificationClick(item);
                    }}
                    sx={{
                      opacity: item.is_read ? 0.72 : 1,
                      bgcolor: item.is_read ? "transparent" : "action.hover",
                    }}
                  >
                    <ListItemText
                      primary={
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          {!item.is_read ? (
                            <Box
                              sx={{
                                width: 8,
                                height: 8,
                                borderRadius: "50%",
                                bgcolor: "primary.main",
                                flexShrink: 0,
                              }}
                            />
                          ) : null}
                          <Typography variant="body2" fontWeight={item.is_read ? 500 : 700}>
                            {title}
                          </Typography>
                        </Box>
                      }
                      secondary={
                        <>
                          <Typography variant="caption" color="text.secondary" component="span" display="block">
                            {body}
                          </Typography>
                          <Typography variant="caption" color="text.disabled" component="span" display="block">
                            {formatRelativeTime(item.created_at, i18n.language)}
                          </Typography>
                        </>
                      }
                    />
                  </ListItemButton>
                );
              })}
            </List>
            {items.length > 0 ? (
              <>
                <Divider />
                <Box sx={{ px: 2, py: 1, display: "flex", justifyContent: "center" }}>
                  <Button size="small" fullWidth onClick={handleSeeMore}>
                    {t("notifications.seeMore")}
                  </Button>
                </Box>
              </>
            ) : null}
          </>
        )}
      </Popover>
    </>
  );
}
